import {
  useCallback,
  useState,
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useRef,
  useMemo,
} from "react";
import {
  Control,
  Controller,
  FieldErrors,
  RegisterOptions,
} from "react-hook-form";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { cn } from "@/lib/utils";
import { Badge } from "../shadcn/badge";
import { Button } from "../shadcn/button";
import { Spinner } from "../UI/Spinner";
import { Input } from "../shadcn/input";
import { X } from "lucide-react";

const Chip = ({
  value,
  variant = "default",
  isInvalid,
  isValidating,
  isClosable,
  onCloseClick,
}: {
  value: string;
  variant?: "default" | "destructive" | "outline" | "secondary";
  isInvalid: boolean;
  isValidating: boolean;
  isClosable?: boolean;
  onCloseClick: () => void;
}) => (
  <Badge className="" variant={isInvalid ? "destructive" : variant}>
    {value}
    {isValidating ? (
      <Spinner size={"xs"} className="ml-1" />
    ) : (
      <>
        {isClosable && (
          <Button
            className="ml-1 h-2 w-2 bg-transparent"
            size="iconxs"
            variant="subtle"
            onClick={onCloseClick}
          >
            <X className="h-2 w-2" strokeWidth="1.33px" />
          </Button>
        )}
      </>
    )}
  </Badge>
);

interface IChipInputProps {
  label?: string;
  prefix?: string;
  placeholder?: string;
  value?: string[];
  helperText?: string;
  areChipsClosable?: boolean;
  maxValues?: number;
  onChange: (value: string[]) => void;
  validationFn?: (values: string[]) => Promise<string[]>;
  isDisabled?: boolean;
  hasError?: boolean;
  isOptional?: boolean;
}

const ChipInput = (props: IChipInputProps) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [invalidValues, setInvalidValues] = useState<string[]>([]);
  const [validatingValues, setValidatingValues] = useState<string[]>([]);

  const currentValues = useMemo(() => props.value || [], [props.value]);

  const inputRef = useRef<HTMLInputElement>(null);

  // Add values to the array of values
  const addValues = useCallback(
    (values: string[]) => {
      if (props.maxValues && props.maxValues === 1 && values.length === 0) {
        props.onChange([""]);
        return;
      }
      let newValues = values.filter((word) => word !== "");

      // Add prefix
      if (props.prefix) {
        newValues = newValues.map((val) =>
          val[0] === props.prefix ? val : `${props.prefix}${val}`
        );
      }

      // Filter repeated values
      newValues = newValues.filter(
        (val: string) => !currentValues.includes(val)
      );

      //Validate new values?
      if (props.validationFn != undefined) {
        setValidatingValues([...newValues]);
        props
          .validationFn(newValues)
          .then((res) => {
            setInvalidValues([...invalidValues, ...res]);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            setValidatingValues([]);
          });
      }

      // Update values
      const allValues = [...currentValues, ...newValues];
      setInputValue("");
      props.onChange(allValues);
    },
    [invalidValues, props, currentValues]
  );

  // Remove a value from the list.
  const removeValue = useCallback(
    (index: number) => {
      if (index !== -1) {
        const valueToRemove = currentValues[index];
        const newValues = [...currentValues];
        newValues.splice(index, 1);
        props.onChange(newValues);

        // When the value is removed it should also be removed from the invalidValues array
        if (invalidValues.includes(valueToRemove)) {
          setInvalidValues([
            ...invalidValues.filter((val) => val != valueToRemove),
          ]);
        }
      }
    },
    [invalidValues, props, currentValues]
  );

  // Handle events functions
  const handleClick = () => {
    if (props.isDisabled) {
      return;
    }

    if (props.maxValues === 1 && currentValues.length === 1) {
      setInputValue(currentValues[0]);
      props.onChange([]);
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!props.maxValues || props.maxValues > currentValues.length) {
      setInputValue(e.target.value);
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !inputValue && currentValues.length > 0) {
        e.preventDefault();
        removeValue(currentValues.length - 1);
      }
      if (["Enter", "Tab"].includes(e.code) && inputValue) {
        e.preventDefault();
        addValues([inputValue]);
      }
    },
    [addValues, inputValue, removeValue, currentValues.length]
  );

  const handleBlur = useCallback(() => {
    addValues([inputValue]);
  }, [addValues, inputValue]);

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();

      const pastedData = e.clipboardData.getData("text");
      const pastedValues = pastedData.split(/[,]+/).slice(0, props.maxValues); // Split ,
      addValues(
        typeof pastedValues === "string" ? [pastedValues] : pastedValues
      );
    },
    [addValues, props.maxValues]
  );

  const handleCloseClick = useCallback(
    (index: number) => removeValue(index),
    [removeValue]
  );

  return (
    <div className="w-full">
      {/* Label */}
      {props.label && (
        <NoxusFormLabel
          label={props.label}
          isRequired={!props.isOptional}
          showOptionalLabel={props.isOptional}
        />
      )}

      {/* Input */}
      <div
        className={cn(
          "relative h-unset flex items-center border border-border! rounded-md w-full overflow-auto flex-wrap shadow-xs px-2",
          "focus-within:ring-ring! focus-within:ring-1!",
          props.isDisabled && "opacity-50 cursor-not-allowed",
          props.hasError && "border-destructive bg-destructive/10",
          props.maxValues === 1 &&
            currentValues.length === 1 &&
            "overflow-hidden flex-nowrap"
        )}
        onClick={handleClick}
      >
        {currentValues.length > 0 && (
          <div className="flex gap-2 py-1 flex-wrap">
            {currentValues.map((value, index) => (
              <Chip
                value={value}
                key={`${index}-${value}`}
                variant={"secondary"}
                isValidating={validatingValues.includes(value)}
                isInvalid={invalidValues.includes(value)}
                isClosable={props.areChipsClosable && !props.isDisabled}
                onCloseClick={() => handleCloseClick(index)}
              />
            ))}
          </div>
        )}

        <div className="flex-grow">
          <Input
            className={cn(
              "px-0 border-none! outline-none! ring-none! shadow-none focus-visible:ring-0! focus-visible:border-none! max-w-unset min-w-32",
              props.isDisabled && "cursor-not-allowed opacity-100!",
              props.maxValues === 1 &&
                currentValues.length === 1 &&
                "min-w-0 max-w-8"
            )}
            ref={inputRef}
            value={inputValue}
            placeholder={
              currentValues.length > 0 ? undefined : props.placeholder
            }
            disabled={validatingValues.length > 0 || props.isDisabled}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
      </div>

      {/* Bottom helper text */}
      {props.helperText && (
        <div className="mt-1.5 text-xs font-normal text-muted-foreground">
          {props.helperText}
        </div>
      )}

      {/* Error message for chip validation */}
      {invalidValues.length > 0 && (
        <div className="mt-1.5 text-xs font-normal text-destructive">
          Some of the provided values are invalid.
        </div>
      )}
    </div>
  );
};

/* For now we are not doing the validation through the rules for this particular component to simplify
(the validation affects the filter values individually) */
interface IChipInputFieldProps {
  name: string;
  control: Control<any>;
  label?: string;
  helperText?: string;
  defaultValue?: string;
  placeholder?: string;
  prefix?: string;
  areChipsClosable?: boolean;
  rules?: RegisterOptions;
  errors?: FieldErrors<any>;
  errorMessage?: string | null;
  validationFn?: (values: string[]) => Promise<string[]>;
  isDisabled?: boolean;
}

export const ChipInputField = (props: IChipInputFieldProps) => {
  return (
    <div className="w-full gap-1 flex flex-col items-start">
      <Controller
        defaultValue={props.defaultValue || ""}
        control={props.control}
        name={props.name}
        rules={props.rules}
        render={({ field: { onChange, value } }) => {
          return (
            <ChipInput
              value={value ? [value] : []}
              label={props.label}
              helperText={props.helperText}
              placeholder={props.placeholder}
              prefix={props.prefix}
              maxValues={1}
              areChipsClosable={props.areChipsClosable}
              validationFn={props.validationFn}
              isDisabled={props.isDisabled}
              onChange={(value: string[]) => {
                onChange(value?.length > 0 ? value[0] : "");
              }}
            />
          );
        }}
      />
      {props.errorMessage && (
        <div className="mt-1.5 text-xs font-normal text-destructive">
          {props.errorMessage}
        </div>
      )}
    </div>
  );
};

interface IChipInputListFieldProps {
  name: string;
  control: Control<any>;
  label?: string;
  helperText?: string;
  defaultValue?: string[];
  placeholder?: string;
  prefix?: string;
  areChipsClosable?: boolean;
  rules?: RegisterOptions;
  errors?: FieldErrors<any>;
  errorMessage?: string | null;
  maxValues?: number;
  validationFn?: (values: string[]) => Promise<string[]>;
  isDisabled?: boolean;
  isOptional?: boolean;
}

export const ChipInputListField = (props: IChipInputListFieldProps) => {
  return (
    <div className="w-full gap-1 flex flex-col items-start">
      <Controller
        defaultValue={props.defaultValue}
        control={props.control}
        name={props.name}
        rules={props.rules}
        render={({ field: { onChange, value } }) => (
          <ChipInput
            value={value}
            label={props.label}
            helperText={props.helperText}
            placeholder={props.placeholder}
            prefix={props.prefix}
            maxValues={props.maxValues}
            areChipsClosable={props.areChipsClosable}
            validationFn={props.validationFn}
            isDisabled={props.isDisabled}
            onChange={onChange}
            isOptional={props.isOptional}
          />
        )}
      />
      {props.errorMessage && (
        <div className="mt-1.5 text-xs font-normal text-destructive">
          {props.errorMessage}
        </div>
      )}
    </div>
  );
};
